import {
  css, type SerializedStyles,
} from '@emotion/react';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import {
  AnimatePresence, motion,
} from 'motion/react';
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import {
  type FC, type ReactNode, useState,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import {
  getScrollableElementFromScrollbarRef, scrollToTop,
} from '~/_shared/components/scrollbar/utils';
import { useTranslation } from '~/_shared/utils/hooks';
import { MapToolWrapperComponent } from '~/sidebar/sidebarApps/mapToolWrapper/mapToolWrapper.component';
import { useTheme } from '../../themes/theme.hooks';
import { type Theme } from '../../themes/theme.model';
import { nameOf } from '../../utils/nameOf';
import { defaultMultilevelMenuItemRenderer } from './defaultMultilevelMenuItemRenderer';
import {
  type BreadcrumbItem, MultilevelMenuBreadcrumbs,
} from './multilevelMenuBreadcrumbs.component';

type MultilevelMenuItemCommon = Readonly<{
  name: string;
  indent?: number;
  renderSelf?: (name: string, onClick: () => void, index: number) => ReactNode;
  style?: SerializedStyles;
  onClick?: () => void;
}>;

type MultilevelMenuItemFinal = Readonly<MultilevelMenuItemCommon & {
  renderChild: () => ReactNode;
}>;

type MultilevelMenuItemMiddle = Readonly<MultilevelMenuItemCommon & {
  children: ReadonlyArray<MultilevelMenuItemMiddle | MultilevelMenuItemFinal>;
}>;

export type MultilevelMenuItem = MultilevelMenuItemMiddle | MultilevelMenuItemFinal;

type MultilevelMenuProps = Readonly<{
  className?: string;
  breadcrumbsStyle?: SerializedStyles;
  footer?: ReactNode;
  items: ReadonlyArray<MultilevelMenuItem>;
  rootBreadcrumbName: string;

  onBack?: () => void;
}>;

const isFinalItem = (item: MultilevelMenuItem): item is MultilevelMenuItemFinal =>
  item.hasOwnProperty(nameOf<MultilevelMenuItemFinal>('renderChild'));

const renderMenu = (items: ReadonlyArray<MultilevelMenuItem>, onClick: (index: number) => void, theme: Theme) =>
  items.map((item, index) =>
    item?.renderSelf?.(item.name, () => onClick(index), index) ?? defaultMultilevelMenuItemRenderer({
      theme,
      name: item.name,
      onClick: () => {
        onClick(index);
        item.onClick?.();
      },
      index,
      style: item.style,
      indent: item.indent,
    })
  );

const findCurrentPage = (root: MultilevelMenuItem, path: ReadonlyArray<number>): MultilevelMenuItem =>
  path.reduce((currentPage: MultilevelMenuItem, currentIndex) =>
    isFinalItem(currentPage) ? currentPage : currentPage.children[currentIndex] || currentPage,
  root,
  );

const renderPage = (page: MultilevelMenuItem, onClick: (index: number) => void, theme: Theme): ReactNode =>
  isFinalItem(page) ? page.renderChild() : renderMenu(page.children, onClick, theme);

const createBreadcrumbs = (
  root: MultilevelMenuItem,
  path: ReadonlyArray<number>,
  setNewPath: (newPath: ReadonlyArray<number>) => void,
  onClick?: () => void,
): ReadonlyArray<BreadcrumbItem> =>
  [...path, 0].map((_, index) => {
    const item = findCurrentPage(root, path.slice(0, index));

    return { name: item.name, onClick: () => {
      setNewPath(path.slice(0, index));
      onClick?.();
    } };
  });

const displayGridStyle = css(`
  display: -ms-grid;
  display: grid;
`);

const rootStyle = css(displayGridStyle, {
  gridTemplateColumns: '100%',
  msGridColumns: '100%',
  gridTemplateRows: '0 100%',
  msGridRows: '0 100%',
  overflow: 'hidden',
});

const secondaryPageCommonStyle = css({
  position: 'relative',
  gridArea: '2/1/2/1',
  msGridRow: 2,
  msGridColumn: 1,
});

const breadCrumbsGroupStyle = css({
  display: 'flex',
  alignItems: 'center',
});

const backIconStyle = (theme: Theme) => css({
  borderRadius: 5,
  cursor: 'pointer',
  marginLeft: 5,
  padding: 5,

  '&:hover': {
    backgroundColor: theme.backgroundColors.tertiary,
  },
});

export const MultiLevelMenuComponent: FC<MultilevelMenuProps> = props => {
  const [path, setPath] = useState<ReadonlyArray<number>>([]);
  const [scrollbarRef, setScrollbarRef] = useState<OverlayScrollbarsComponentRef>();
  const theme = useTheme();
  const [t] = useTranslation();

  const rootPage = { name: props.rootBreadcrumbName, children: props.items };

  const handleClickItem = (index: number, item: ReadonlyArray<number>) => {
    scrollToTop(getScrollableElementFromScrollbarRef(scrollbarRef));
    setPath([...item, index]);
  };

  return (
    <MapToolWrapperComponent
      className={props.className}
      footer={props.footer}
      getScrollbarReference={setScrollbarRef}
      header={(
        <div css={breadCrumbsGroupStyle} >
          {props.onBack && (
            <FontAwesomeIcon
              css={backIconStyle}
              icon={faChevronLeft}
              onClick={props.onBack}
              title={t('Back')}
            />
          )}
          <MultilevelMenuBreadcrumbs
            breadcrumbs={createBreadcrumbs(rootPage, path, setPath, () => {
              scrollToTop(getScrollableElementFromScrollbarRef(scrollbarRef));
            })}
            wrapperStyle={props.breadcrumbsStyle}
          />
        </div>
      )}
    >
      <div css={rootStyle}>
        <AnimatePresence mode="wait">
          <motion.div
            key={JSON.stringify(path)}
            css={secondaryPageCommonStyle}
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -100, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {renderPage(findCurrentPage(rootPage, path), i => handleClickItem(i, path), theme)}
          </motion.div>
        </AnimatePresence>
      </div>
    </MapToolWrapperComponent>
  );
};

MultiLevelMenuComponent.displayName = 'MultiLevelMenuComponent';
